import React, { PureComponent, ReactNode } from "react";
import withRenderOn from "src/hoc/withRenderOn";
import { IRenderOn } from "src/hooks/useRenderOn";
import styled from "styled-components";

import { ICartCouponCodeRemoveProps } from "../../../../hoc/persister/cartCouponCodeRemove/cartCouponCodeRemove";
import { ICartGiftCardRemoveProps } from "../../../../hoc/persister/cartGiftCardRemove/cartGiftCardRemove";
import { ICartUpdateProps } from "../../../../hoc/persister/cartUpdate/cartUpdate";
import toaster from "../../../../lib/toaster";
import { formatPrice } from "../../../../services/number.service";
import { translate } from "../../../../services/translation.service";
import theme from "../../../../theme";
import { ICartProductDetail, IEstimatedShipping } from "../../../../types/cart";
import { IDeliveryMode } from "../../../../types/deliveryMode";
import { IGiftCard } from "../../../../types/giftCard";
import Icon from "../../../Icon";

const Wrapper = styled.article`
  display: flex;
`;

const RemoveIcon = styled(Icon)`
  margin-left: -4px;
`;

const RowsWrapper = styled.ul`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: center;
  margin: 0;
  padding: 0;
  list-style: none;
`;

interface IRowWrapperProps {
  bold?: boolean;
}

const RowWrapper = styled.li<IRowWrapperProps>`
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  font-family: ${({ bold }: IRowWrapperProps): string =>
    bold ? theme.fonts.subtitle : theme.fonts.regular};

  &:not(:first-of-type) {
    margin-top: ${theme.margins.marginMedium};
  }
`;

const Row = styled.div`
  font-size: ${theme.fontSizes.medium};
  display: flex;
  align-items: center;
`;

export interface ICouponCode {
  code: string | null;
  amount: number | null;
}

export interface IProps
  extends ICartUpdateProps,
    ICartCouponCodeRemoveProps,
    ICartGiftCardRemoveProps {
  className?: string;
  coupon?: ICouponCode;
  deliveryMode?: IDeliveryMode;
  estimatedShipping: IEstimatedShipping;
  giftCards?: IGiftCard[];
  items: ICartProductDetail[];
  subTotal: number;
  taxAmount: number;
  total: number;
  renderOnProps?: IRenderOn;
}

class PriceRecap extends PureComponent<IProps> {
  public static defaultProps: Partial<IProps> = {
    className: "",
    giftCards: [],
  };

  public computeNbItems = (items: ICartProductDetail[]): number => {
    return items.reduce(
      (totalItems: number, item: ICartProductDetail) =>
        totalItems + item.quantity,
      0
    );
  };

  public removeCouponCode = async (code: string): Promise<void> => {
    const { error } = await this.props.cartCouponCodeRemove({ code });
    if (!error) {
      toaster.notify(translate("cart.couponCodeRemoved"));
    }
  };

  public removeGiftCard = async (code: string): Promise<void> => {
    const { error } = await this.props.cartGiftCardRemove({ code });
    if (!error) {
      toaster.notify(translate("cart.giftCardRemoved"));
    }
  };

  public render(): ReactNode {
    const {
      className,
      coupon,
      deliveryMode,
      estimatedShipping,
      giftCards,
      items,
      subTotal,
      taxAmount,
      total,
      renderOnProps: { mdOrDown = false } = {},
    } = this.props;

    const totalItems = this.computeNbItems(items);

    return (
      <Wrapper className={className}>
        <RowsWrapper>
          <RowWrapper bold>
            <Row>
              {translate("cart.subTotal")} (
              {translate("cart.products", {
                count: totalItems,
              })}
              )
            </Row>
            <Row>{formatPrice(subTotal)} €</Row>
          </RowWrapper>
          {mdOrDown && (
            <>
              <RowWrapper>
                <Row>{translate("cart.includingTax")}</Row>
                <Row>{formatPrice(taxAmount)} €</Row>
              </RowWrapper>
              {this.renderCouponCode(coupon)}
              {this.renderGiftCards(giftCards)}
              {this.renderDeliveryModeAmount(deliveryMode)}
              {this.renderTotal(
                total,
                coupon,
                giftCards,
                deliveryMode,
                estimatedShipping
              )}
              {this.renderEstimatedShippingAmount(
                deliveryMode,
                estimatedShipping
              )}
            </>
          )}
        </RowsWrapper>
      </Wrapper>
    );
  }

  public renderCouponCode(coupon?: ICouponCode): ReactNode {
    if (!coupon) {
      return null;
    }

    const { code, amount } = coupon;
    if (code !== null && amount !== null && typeof amount !== "undefined") {
      return (
        <RowWrapper>
          <Row>
            <RemoveIcon
              name="cross"
              size={24}
              onClick={async (): Promise<void> => this.removeCouponCode(code)}
            />{" "}
            Code promo {code}
          </Row>
          <Row>{formatPrice(amount)} € </Row>
        </RowWrapper>
      );
    }

    return null;
  }

  public renderDeliveryModeAmount(deliveryMode?: IDeliveryMode): ReactNode {
    if (deliveryMode != null) {
      return (
        <RowWrapper>
          <Row>
            {translate("shipping.shippingMethod")} {deliveryMode.name}
          </Row>
          <Row>{formatPrice(deliveryMode.price)} €</Row>
        </RowWrapper>
      );
    }

    return null;
  }

  public renderEstimatedShippingAmount(
    deliveryMode?: IDeliveryMode,
    estimatedShipping?: IEstimatedShipping
  ): ReactNode {
    if (deliveryMode == null && estimatedShipping != null) {
      return (
        <RowWrapper>
          <Row>
            {translate("cart.estimatedShippingTime")} ({estimatedShipping.mode})
          </Row>
          <Row>{formatPrice(estimatedShipping.price)} €</Row>
        </RowWrapper>
      );
    }

    return null;
  }

  public renderGiftCards(giftCards?: IGiftCard[]): ReactNode {
    if (giftCards != null) {
      return giftCards.map((giftCard: IGiftCard) => (
        <RowWrapper key={giftCard.id}>
          <Row>
            <RemoveIcon
              name="cross"
              size={24}
              onClick={async (): Promise<void> =>
                this.removeGiftCard(giftCard.code)
              }
            />{" "}
            Carte {giftCard.code}
          </Row>
          <Row>
            -
            {formatPrice(
              giftCard.amount >= this.props.subTotal
                ? this.props.subTotal
                : giftCard.amount
            )}{" "}
            €{" "}
          </Row>
        </RowWrapper>
      ));
    }

    return null;
  }

  public renderTotal(
    total: number,
    coupon?: ICouponCode,
    giftCards?: IGiftCard[],
    deliveryMode?: IDeliveryMode,
    estimatedShipping?: IEstimatedShipping
  ): ReactNode {
    if (
      (coupon && coupon.code) ||
      (giftCards != null && giftCards.length > 0) ||
      deliveryMode != null
    ) {
      total =
        estimatedShipping && estimatedShipping.price
          ? total + estimatedShipping.price
          : total;

      return (
        <RowWrapper bold>
          <Row>{translate("cart.total")}</Row>
          <Row>{formatPrice(total)} €</Row>
        </RowWrapper>
      );
    }

    return null;
  }
}

export default withRenderOn(PriceRecap);
